import { useState } from "react";
import {
  Activity,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  ListOrdered,
  Users,
} from "lucide-react";
import { NavLink, Outlet } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AnalyticsRangeProvider,
  RANGES,
  useAnalyticsData,
  type AnalyticsSource,
} from "@/lib/analytics/range";

const SOURCES: { key: AnalyticsSource; label: string }[] = [
  { key: "demo", label: "Demo" },
  { key: "live", label: "Live" },
];

/** Demo / Live switch — sits with the range picker as one filter group. */
function SourceSwitch({ collapsed }: { collapsed: boolean }) {
  const { source, setSource, canUseLive } = useAnalyticsData();
  if (!canUseLive) return null;

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setSource(source === "live" ? "demo" : "live")}
        title={source === "live" ? "Live data — switch to demo" : "Demo data — switch to live"}
        aria-label={source === "live" ? "Showing live data" : "Showing demo data"}
        className={cn(
          "flex w-full items-center justify-center rounded-lg border py-2 transition-colors",
          source === "live"
            ? "border-primary/40 bg-primary/10 text-primary"
            : "bg-card text-muted-foreground hover:bg-accent",
        )}
      >
        <Activity className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div
      role="group"
      aria-label="Data source"
      className="flex w-full rounded-lg border bg-card p-0.5"
    >
      {SOURCES.map((s) => (
        <button
          key={s.key}
          type="button"
          aria-pressed={source === s.key}
          onClick={() => setSource(s.key)}
          className={cn(
            "flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors",
            source === s.key
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Live data has real async states, so the layout owns them once for every
 * analytics page instead of each page re-implementing skeletons and empties.
 */
function AnalyticsContent() {
  const { source, isLoading, error, refetch, sessions } = useAnalyticsData();

  if (source === "demo") return <Outlet />;

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center">
        <h2 className="text-lg font-semibold text-foreground">Analytics couldn’t load</h2>
        <p className="max-w-md text-sm text-muted-foreground">{error}</p>
        <Button onClick={refetch}>Try again</Button>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center">
        <h2 className="text-lg font-semibold text-foreground">No sessions in this range</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          Nothing was recorded for your scenarios in the selected date range. Pick a wider range, or
          switch to Demo to explore the layout with sample data.
        </p>
      </div>
    );
  }

  return <Outlet />;
}

function AnalyticsShell() {
  const [collapsed, setCollapsed] = useState(false);
  const { avatars, sessions, range, rangeLabel, setRange, source } = useAnalyticsData();

  const items = [
    { to: "/trainer/analytics", label: "Overview", icon: LayoutDashboard, count: undefined as number | undefined, end: true },
    { to: "/trainer/analytics/avatars", label: "Avatars", icon: Users, count: avatars.length, end: false },
    { to: "/trainer/analytics/sessions", label: "Sessions", icon: ListOrdered, count: sessions.length, end: false },
  ];


  return (
    <div className="analytics-nav-scope flex w-full min-h-full">
      <aside
        className={cn(
          "shrink-0 border-r bg-sidebar transition-[width] duration-200 ease-linear",
          collapsed ? "w-14" : "w-64",
        )}
      >
        <div className={cn("flex items-center p-2", collapsed ? "justify-center" : "justify-end")}>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        <div className="mb-2 px-2">
          <SourceSwitch collapsed={collapsed} />
        </div>

        <div className="px-2">
          <DropdownMenu>

            <DropdownMenuTrigger asChild>
              <button
                type="button"
                title={rangeLabel}
                aria-label={`Date range: ${rangeLabel}`}
                className={cn(
                  "flex w-full items-center gap-2 rounded-lg border bg-card py-2 text-sm font-semibold text-foreground transition-colors hover:bg-accent",
                  collapsed ? "justify-center px-0" : "px-3",
                )}
              >
                <CalendarDays className="h-4 w-4 shrink-0" />
                {!collapsed && (
                  <>
                    <span>{rangeLabel}</span>
                    <ChevronDown className="ml-auto h-4 w-4" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              {RANGES.map((r) => (
                <DropdownMenuItem key={r.key} onSelect={() => setRange(r.key)}>
                  <span>{r.label}</span>
                  {range === r.key && <Check className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <nav className="mt-3 flex flex-col gap-1 px-2" aria-label="Analytics">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              title={item.label}
              className={({ isActive }) =>
                cn(
                  "analytics-nav-item",
                  collapsed ? "justify-center px-0" : undefined,
                  isActive && "is-active",
                )
              }
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {!collapsed && (
                <>
                  <span>{item.label}</span>
                  {item.count != null && (
                    <span className="analytics-nav-count">{item.count}</span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="min-w-0 flex-1">
        {source === "live" && (
          <div className="border-b bg-primary/5 px-6 py-1.5 text-xs font-medium text-primary">
            Showing live session data
          </div>
        )}
        <AnalyticsContent />
      </div>

    </div>
  );
}

export function AnalyticsLayout() {
  return (
    <AnalyticsRangeProvider>
      <AnalyticsShell />
    </AnalyticsRangeProvider>
  );
}
